import { ChangeDetectionStrategy, Component, HostListener, TemplateRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideChevronDown, lucideChevronRight, lucideChevronUp, lucideExternalLink } from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { AnalysisViewer, ViewerSelection } from '@shared/components/analysis-viewer/analysis-viewer';
import { ColumnDef, DataTableComponent } from '@shared/components/data-table/data-table';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { SidePanel } from '@shared/components/side-panel/side-panel';
import { StatePanel } from '@shared/components/state-panel/state-panel';
import { Analysis, AnalysisPlanning, FileOverviewItem } from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { FilterRail } from '@shared/components/filter-rail/filter-rail';
import { StageChip } from '../stage-chip/stage-chip';
import { PLANNING_OUTCOMES, REVIEW_STAGES, STAGE_META, WorkItem, WorkflowStage, moneyFigure, powerFigure, splitAddress } from '../../models/campaign-workflow.model';

const PAGE_SIZE = 25;
const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const one = (value: number) => value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const whole = (value: number) => Math.round(value).toLocaleString('de-DE');

type Filter = 'all' | 'done' | 'unreviewed' | 'replan' | 'fast' | 'mid' | 'slow';
const hasPlanning = (item: WorkItem) => item.planning.outcome === 'ok' || item.planning.outcome === 'outdated';
const MATCH: Record<Filter, (item: WorkItem) => boolean> = {
  all: (item) => item.stage === 'done' || item.stage === 'replan' || REVIEW_STAGES.includes(item.stage),
  done: (item) => item.stage === 'done',
  unreviewed: (item) => REVIEW_STAGES.includes(item.stage),
  replan: (item) => item.stage === 'replan',
  fast: (item) => item.stage === 'done' && item.planning.payback !== null && item.planning.payback < 10,
  mid: (item) => item.stage === 'done' && item.planning.payback !== null && item.planning.payback >= 10 && item.planning.payback < 15,
  slow: (item) => item.stage === 'done' && item.planning.payback !== null && item.planning.payback >= 15,
};
const STAND: { id: Filter; label: string; stage?: WorkflowStage; tip?: string }[] = [
  { id: 'all', label: 'Alle Planungen' },
  { id: 'done', label: 'Fertig', stage: 'done', tip: 'Geprüft und aktuell.' },
  { id: 'unreviewed', label: 'Noch ungeprüft', stage: 'review', tip: 'Die Planung ist da, die Erkennung wurde noch nicht bestätigt. Diese Analysen stehen in der Phase Prüfen.' },
  { id: 'replan', label: 'Neu zu planen', stage: 'replan', tip: 'Korrigiert. Diese Planung gilt noch für den alten Stand.' },
];
const PAYBACK: { id: Filter; label: string }[] = [
  { id: 'fast', label: 'Unter 10 Jahren' },
  { id: 'mid', label: '10 bis 15 Jahre' },
  { id: 'slow', label: 'Über 15 Jahre' },
];

/** All plannings at a glance; one opens beside the table, with what it means and what to do about it. */
@Component({
  selector: 'app-campaign-planning-results-tab',
  standalone: true,
  imports: [RouterLink, NgIconComponent, HlmButtonDirective, AnalysisViewer, DataTableComponent, InlineNotice, FilterRail, SidePanel, StatePanel, StageChip],
  providers: [provideIcons({ lucideChevronDown, lucideChevronRight, lucideChevronUp, lucideExternalLink })],
  templateUrl: './campaign-planning-results-tab.html',
  host: { class: 'flex flex-col gap-6' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignPlanningResultsTab {
  readonly store = inject(CampaignWorkflowStore);
  private readonly analyses = inject(AnalysisService);
  private readonly org = inject(CurrentOrganizationStore);

  readonly stageMeta = STAGE_META;
  readonly outcomes = PLANNING_OUTCOMES;
  readonly planningView: ViewerSelection = { view: 'image', image: 'planung', model: 'with-pv' };
  readonly pageSize = PAGE_SIZE;
  readonly addressTpl = viewChild.required<TemplateRef<unknown>>('addressTpl');
  readonly stageTpl = viewChild.required<TemplateRef<unknown>>('stageTpl');
  readonly table = viewChild<DataTableComponent<WorkItem>>(DataTableComponent);

  readonly filter = signal<Filter>('done');
  readonly selectedId = signal<string | null>(null);
  readonly analysis = signal<Analysis | null>(null);
  readonly plannings = signal<AnalysisPlanning[]>([]);
  readonly files = signal<FileOverviewItem[]>([]);
  readonly monthly = signal<number[]>([]);
  readonly detailLoading = signal(false);
  readonly detailError = signal<string | null>(null);
  private request = 0;

  private readonly planned = computed(() => (this.store.items() ?? []).filter(hasPlanning));
  private readonly hasFinance = computed(() => this.planned().some((item) => item.planning.payback !== null));
  private countOf = (id: Filter) => this.planned().filter(MATCH[id]).length;

  readonly groups = computed(() => [
    { label: 'Stand', rows: STAND.map((row) => ({ ...row, count: this.countOf(row.id), color: row.stage ? STAGE_META[row.stage].color : null })) },
    ...(this.hasFinance() ? [{ label: 'Amortisation', rows: PAYBACK.map((row) => ({ ...row, count: this.countOf(row.id), color: null })) }] : []),
  ]);

  readonly rows = computed(() => this.planned().filter(MATCH[this.filter()]));
  readonly selected = computed(() => this.store.itemById().get(this.selectedId() ?? '') ?? null);

  readonly kpis = computed(() => {
    const rows = this.rows();
    const kwp = rows.reduce((sum, item) => sum + (item.planning.kwp ?? 0), 0);
    const withInvest = rows.filter((item) => item.planning.invest !== null);
    const withPayback = rows.filter((item) => item.planning.payback !== null);
    const invest = withInvest.reduce((sum, item) => sum + item.planning.invest!, 0);
    const payback = withPayback.reduce((sum, item) => sum + item.planning.payback!, 0);
    return [
      { label: 'Anlagen', value: rows.length.toLocaleString('de-DE'), unit: '' },
      { label: 'Leistung', ...(rows.length ? powerFigure(kwp) : { value: '–', unit: '' }) },
      { label: 'Investition', ...(withInvest.length ? moneyFigure(invest) : { value: '–', unit: '' }) },
      { label: 'Ø Amortisation', value: withPayback.length ? one(payback / withPayback.length) : '–', unit: withPayback.length ? 'Jahre' : '' },
    ];
  });

  readonly columns = computed<ColumnDef<WorkItem>[]>(() => [
    { header: 'Adresse', template: this.addressTpl(), searchValue: (item) => item.title ?? '', sortValue: (item) => item.title },
    { header: 'Stand', template: this.stageTpl(), class: 'w-[124px]', sortValue: (item) => item.stage },
    { header: 'Leistung', cell: (item) => (item.planning.kwp !== null ? `${one(item.planning.kwp)} kWp` : '–'), class: 'w-[92px] whitespace-nowrap text-right font-mono', sortValue: (item) => item.planning.kwp },
    { header: 'Module', cell: (item) => String(item.planning.modules ?? '–'), class: 'w-[72px] text-right font-mono', sortValue: (item) => item.planning.modules },
    ...(this.hasFinance() ? [
      { header: 'Investition', cell: (item: WorkItem) => (item.planning.invest !== null ? `${whole(item.planning.invest)} €` : '–'), class: 'w-[104px] whitespace-nowrap text-right font-mono', sortValue: (item: WorkItem) => item.planning.invest },
      { header: 'Amortisation', cell: (item: WorkItem) => (item.planning.payback !== null ? `${one(item.planning.payback)} J` : '–'), class: 'w-[108px] whitespace-nowrap text-right font-mono', sortValue: (item: WorkItem) => item.planning.payback },
      { header: 'Rendite', cell: (item: WorkItem) => (item.planning.roi !== null ? `${one(item.planning.roi)} %` : '–'), class: 'w-[72px] whitespace-nowrap text-right font-mono', sortValue: (item: WorkItem) => item.planning.roi },
    ] : []),
  ]);

  readonly rowClass = (item: WorkItem) => (item.id === this.selectedId() ? 'bg-accent' : '');
  readonly getRowId = (item: WorkItem) => item.id;

  /** The planning to show: the current one, or else the latest that worked. */
  readonly planning = computed<AnalysisPlanning | null>(() => {
    const id = this.selected()?.planning.id;
    const plannings = this.plannings();
    return plannings.find((planning) => planning.id === id) ?? plannings.find((planning) => planning.status === 'completed') ?? null;
  });
  readonly marks = computed(() => {
    const item = this.selected();
    if (!item) return [];
    const marks: string[] = [];
    if (item.corrections) marks.push(item.corrections > 1 ? `Korrigiert ×${item.corrections}` : 'Korrigiert');
    if (item.replanned) marks.push('Nachgeplant');
    return marks;
  });
  readonly heading = computed(() => splitAddress(this.selected()?.title ?? null));
  readonly detailKpis = computed(() => {
    const planning = this.planning();
    if (!planning) return [];
    const investment = planning.totalInvestment ?? planning.acquisitionCosts;
    return [
      { key: 'Leistung', value: planning.plantCapacity != null ? `${one(planning.plantCapacity)} kWp` : '–' },
      { key: 'Module', value: String(planning.numberModels ?? '–') },
      { key: 'Investition', value: investment != null ? `${whole(investment)} €` : '–' },
      { key: 'Amortisation', value: planning.amortizationPeriod != null ? `${one(planning.amortizationPeriod)} Jahre` : '–' },
      { key: 'Rendite', value: planning.annualReturn != null ? `${one(planning.annualReturn)} %` : '–' },
      { key: 'CO₂ im Jahr', value: planning.co2Savings != null ? `${whole(planning.co2Savings)} kg` : '–' },
    ];
  });
  readonly facts = computed(() => {
    const planning = this.planning();
    if (!planning) return [];
    const name = (part: unknown) => (part as { name?: string; model?: string } | null | undefined)?.name ?? (part as { model?: string } | null | undefined)?.model ?? null;
    return [
      { key: 'Modul', value: name(planning.module) },
      { key: 'Wechselrichter', value: name(planning.inverter) },
      { key: 'Speicher', value: name(planning.storage) },
      { key: 'Vorlage', value: name(planning.template) },
    ].filter((fact) => fact.value);
  });
  readonly months = computed(() => {
    const values = this.monthly();
    const peak = Math.max(1, ...values);
    return values.slice(0, 12).map((value, index) => ({
      x: MONTHS[index],
      title: `${whole(value)} kWh`,
      height: Math.round((value / peak) * 100),
    }));
  });
  readonly yearly = computed(() => {
    const total = this.monthly().reduce((sum, value) => sum + value, 0);
    return total ? `${whole(total)} kWh im Jahr` : '';
  });
  readonly analysisLink = computed(() => {
    const slug = this.org.organization()?.slug;
    const id = this.selected()?.id;
    return slug && id ? ['/organizations', slug, 'analyses', id] : null;
  });

  constructor() {
    void this.store.loadItems();
    effect(() => {
      const nav = this.store.nav();
      untracked(() => {
        if (nav.filter) this.filter.set(nav.filter as Filter);
        if (nav.focus) void this.select(nav.focus);
      });
    });
  }

  pick(filter: Filter): void {
    this.filter.set(filter);
    this.close();
  }

  close(): void {
    this.request++;
    this.selectedId.set(null);
  }

  async select(id: string): Promise<void> {
    const request = ++this.request;
    this.selectedId.set(id);
    this.analysis.set(null);
    this.plannings.set([]);
    this.monthly.set([]);
    this.detailLoading.set(true);
    this.detailError.set(null);
    const org = this.org.organizationId()!;
    try {
      const [analysis, plannings, files] = await Promise.all([
        this.analyses.getAnalysisDetail(org, id),
        this.analyses.getAnalysisPlannings(org, id),
        this.analyses.getFilesOverview(org, id),
      ]);
      if (request !== this.request) return;
      this.analysis.set(analysis);
      this.plannings.set(plannings);
      this.files.set(files);
      const shown = this.planning();
      if (shown) {
        const chart = await this.analyses.getPlanningChartData(org, id, shown.id).catch(() => null);
        if (request === this.request) this.monthly.set(chart?.charts.coverageMonthGeneration?.values ?? []);
      }
    } catch {
      if (request === this.request) this.detailError.set('Die Planung konnte nicht geladen werden.');
    } finally {
      if (request === this.request) this.detailLoading.set(false);
    }
  }

  step(delta: number): void {
    const rows = this.table()?.limitedData() ?? this.rows();
    const next = rows[rows.findIndex((row) => row.id === this.selectedId()) + delta];
    if (next) void this.select(next.id);
  }

  replan(): void {
    const item = this.selected();
    if (item) this.store.openRun({ single: this.heading().street, ids: [item.id], target: 'replan' });
  }

  correct(): void {
    const item = this.selected();
    if (!item) return;
    const queue = item.replanned && !item.checked ? 'recheck' : item.stage;
    void this.store.go('review', { stage: queue, focus: item.id, view: 'planning' });
  }

  @HostListener('window:keydown', ['$event']) onKeyDown(event: KeyboardEvent): void {
    const element = event.target as HTMLElement | null;
    if (this.store.runPanel() || !this.selectedId() || element?.closest('input, select, textarea, [role="menu"]') || event.metaKey || event.ctrlKey) return;
    const key = event.key.toLowerCase();
    if (key === 'j' || key === 'arrowdown') this.step(1);
    else if (key === 'k' || key === 'arrowup') this.step(-1);
    else if (key === 'r' && this.store.canStart() && !this.store.running()) this.replan();
    else if (key === 'e') this.correct();
    else return;
    event.preventDefault();
  }
}
